import React, { useState } from 'react';
import './CategoriesTab.css';
import useCatalogManagementStore from '@/stores/AdminStore/CatalogManagementStore';
import { Category } from '@/services/PagesServices/CatalogManagementService';

interface ModalState {
  isOpen: boolean;
  type: 'root' | 'subcategory';
  parentCategory?: Category;
}

const CategoriesTab: React.FC = () => {
  const { categories, addCategory, addSubcategory, toggleCategoryExpand } = useCatalogManagementStore();

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: 'root',
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const openAddRootModal = () => {
    setModalState({ isOpen: true, type: 'root' });
    setFormData({ name: '', description: '' });
  };

  const openAddSubcategoryModal = (parentCategory: Category) => {
    setModalState({ isOpen: true, type: 'subcategory', parentCategory });
    setFormData({ name: '', description: '' });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: 'root' });
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (modalState.type === 'root') {
      const newCategory: Category = {
        id: Date.now().toString(),
        name: formData.name,
        description: formData.description,
        courseCount: 0,
        isExpanded: false,
      };
      addCategory(newCategory);
    } else if (modalState.type === 'subcategory' && modalState.parentCategory) {
      const newSubcategory: Category = {
        id: Date.now().toString(),
        name: formData.name,
        description: formData.description,
        courseCount: 0,
      };
      addSubcategory(modalState.parentCategory.id, newSubcategory);
    }

    closeModal();
  };

  const toggleExpand = (categoryId: string) => {
    toggleCategoryExpand(categoryId);
  };

  const renderCategoryItem = (category: Category, isSubcategory: boolean = false) => (
    <div
      key={category.id}
      className={`admin-catalog-category-item ${isSubcategory ? 'admin-catalog-subcategory' : ''}`}
    >
      <div className="admin-catalog-category-content">
        {!isSubcategory && category.subcategories && (
          <button
            className="admin-catalog-expand-button"
            onClick={() => toggleExpand(category.id)}
            aria-label={category.isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className={`admin-catalog-expand-icon ${category.isExpanded ? 'admin-catalog-expanded' : ''}`}
            >
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <div className="admin-catalog-category-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 7l9 6 9-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="admin-catalog-category-info">
          <div className="admin-catalog-category-header">
            <h3 className="admin-catalog-category-name">{category.name}</h3>
            <span className="admin-catalog-course-count">{category.courseCount} courses</span>
          </div>
          <p className="admin-catalog-category-description">{category.description}</p>
        </div>

        <div className="admin-catalog-category-actions">
          <button
            className="admin-catalog-action-button"
            aria-label="Add subcategory"
            onClick={(e) => {
              e.stopPropagation();
              openAddSubcategoryModal(category);
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button className="admin-catalog-action-button" aria-label="Edit category">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button className="admin-catalog-action-button" aria-label="Delete category">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button className="admin-catalog-action-button admin-catalog-menu-button" aria-label="More options">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="1" fill="currentColor" />
              <circle cx="12" cy="5" r="1" fill="currentColor" />
              <circle cx="12" cy="19" r="1" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      {category.isExpanded && category.subcategories && (
        <div className="admin-catalog-subcategories">
          {category.subcategories.map(subcat => renderCategoryItem(subcat, true))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="admin-catalog-section-header">
        <h2 className="admin-catalog-section-title">Category Hierarchy</h2>
        <button className="admin-catalog-add-root-button" onClick={openAddRootModal}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Add Root Category
        </button>
      </div>

      <div className="admin-catalog-categories-list">
        {categories?.map(category => renderCategoryItem(category))}
      </div>

      {/* Modal for Add Category/Subcategory */}
      {modalState.isOpen && (
        <div className="admin-catalog-modal-overlay" onClick={closeModal}>
          <div className="admin-catalog-modal-content" onClick={(e) => e.stopPropagation()}>
            {modalState.type === 'subcategory' && modalState.parentCategory && (
              <div className="admin-catalog-modal-parent-info">
                <div className="admin-catalog-parent-category-badge">
                  <div className="admin-catalog-category-icon admin-catalog-small">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 7l9 6 9-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="admin-catalog-parent-info">
                    <span className="admin-catalog-parent-name">{modalState.parentCategory.name}</span>
                    <span className="admin-catalog-parent-course-count">{modalState.parentCategory.courseCount} courses</span>
                  </div>
                  <span className="admin-catalog-parent-description">{modalState.parentCategory.description}</span>
                </div>
              </div>
            )}

            <h2 className="admin-catalog-modal-title">
              {modalState.type === 'root' ? 'Add Category' : 'Add Subcategory'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="categoryName" className="admin-catalog-form-label">
                  Category Name <span className="admin-catalog-required">*</span>
                </label>
                <input
                  type="text"
                  id="categoryName"
                  className="admin-catalog-form-input"
                  placeholder="e.g., Technical Skills"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="admin-catalog-form-group">
                <label htmlFor="description" className="admin-catalog-form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  className="admin-catalog-form-textarea"
                  placeholder="Brief description of this category..."
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="submit" className="admin-catalog-btn-save">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {modalState.type === 'root' ? 'Save Category' : 'Save Sub-Category'}
                </button>
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoriesTab;
